import React, { createContext, useContext, useMemo } from 'react';

/**
 * The homepage menu context (ECOMM-11826).
 *
 * On a Dutchie-hosted, multi-store enterprise domain, `/` resolves the hostname →
 * enterprise and defaults the visitor into their nearest store (or the configured
 * default location, C3). That resolved store is the menu context for the homepage:
 * downstream menu-side surfaces (CMS resolve targeting, menu landing, etc.) read
 * the enterprise + dispensary from here instead of a cName in the URL path, which
 * the global homepage doesn't have.
 */
export type HomeMenuContextValue = {
  enterpriseId: string;
  /** Nearest store by geolocation, or the configured default location (C3). */
  dispensaryId: string;
};

const HomeMenuContext = createContext<HomeMenuContextValue | null>(null);

type HomeMenuProviderProps = HomeMenuContextValue & {
  children: React.ReactNode;
};

export function HomeMenuProvider({ enterpriseId, dispensaryId, children }: HomeMenuProviderProps): JSX.Element {
  const value = useMemo(() => ({ enterpriseId, dispensaryId }), [enterpriseId, dispensaryId]);
  return <HomeMenuContext.Provider value={value}>{children}</HomeMenuContext.Provider>;
}

/**
 * Returns the resolved homepage menu context, or null when the homepage is the
 * plain marketplace homepage (no enterprise domain resolved).
 */
export function useHomeMenuContext(): HomeMenuContextValue | null {
  return useContext(HomeMenuContext);
}
